// `extension-alert-candidates-slim.tpl.html:25` — the "Sort ▾  Filters ▾  👤 433" row above
// the candidate list.
//
// ⚠️ THE TITLE ON THE TWO WRAPPERS IS THE SAME EXPRESSION AS THE ALERT ROW'S, and it is there
// for the same reason: while a bulk operation owns the frame (`data.isCheckboxDependent`) the
// controls are disabled and the tooltip names the window to close. Upstream writes it twice,
// once around each control, rather than once around both — the two spans are separate children
// of the flex row, so wrapping them together would change the layout.
import type { ReactElement, ReactNode } from 'react'
import { uibDropdown } from '../common/uibDropdown.ts'
import {
  activeFilterCount,
  CV_DATE_OPTION_DEFAULT,
  filtersAreModified,
  SORT_OPTIONS,
  type SortOption,
} from './candidateFilters.ts'

/** Both products' options share the two fields this bar reads. */
export interface SortOptionLike {
  display: string
  field: string
}

export interface CandidateFiltersBarProps {
  /** `ctrl.currSortOrder` — the DISPLAY string, which is what the comparison uses. */
  currentSort: string
  /** `ctrl.currCandidateShownOption.name`. */
  shownOptionName: string
  /**
   * `ctrl.currLastRelevancyXDaysOption.name`.
   *
   * ⚠️ OMITTED ON THE CONTACTS SCREEN, WHICH HAS NO CV-DATE FILTER AT ALL — see
   * `clientContactFilters.ts`. Left required-looking would force that screen to invent a value
   * for a control it does not draw, and `activeFilterCount` would then count to 2 where
   * upstream's contacts `getNumberOfActiveFilters` only ever returns 0 or 1.
   */
  cvDateOptionName?: string
  /**
   * `ctrl.sortOptions` — a per-screen list. The contacts screen sorts by prior engagement and
   * hiring likelihood; the candidates screen by relevance and CV date, and they are different
   * enums on the wire.
   */
  sortOptions?: readonly SortOptionLike[]
  /** `ctrl.maxResultsForPaging` — the number beside the user icon. */
  totalResults?: number
  /** `data.isCheckboxDependent` — a bulk operation owns the frame. */
  checkboxDependent?: boolean
  checkboxOperationLabel?: string
  sortOpen?: boolean
  onToggleSort?: () => void
  onSelectSort?: (option: SortOptionLike) => void
  onToggleFilters?: () => void
  /**
   * The menu itself, when it is open. Passed in rather than rendered here, because the bar
   * does not own the filter state — the review route does, and it is what re-queries.
   */
  filtersMenu?: ReactNode
}

export function CandidateFiltersBar({
  currentSort,
  shownOptionName,
  cvDateOptionName,
  sortOptions = SORT_OPTIONS,
  totalResults,
  checkboxDependent = false,
  checkboxOperationLabel,
  sortOpen = false,
  onToggleSort,
  onSelectSort,
  onToggleFilters,
  filtersMenu,
}: CandidateFiltersBarProps): ReactElement {
  // ⚠️ THE FALLBACK IS THE DEFAULT, NOT AN EMPTY STRING. Both helpers compare against
  // `CV_DATE_OPTION_DEFAULT` by name, so `''` would read as "the CV-date filter is set" on
  // every contacts screen and show a count of 1 beside a filter that does not exist.
  const cvDateName = cvDateOptionName ?? CV_DATE_OPTION_DEFAULT
  const modified = filtersAreModified(shownOptionName, cvDateName)
  const activeCount = activeFilterCount(shownOptionName, cvDateName)
  // `ng-attr-title` renders no attribute at all when the expression is null.
  const blockedTitle = checkboxDependent
    ? { title: `Please close the ${checkboxOperationLabel ?? ''} window to continue` }
    : {}

  return (
    <div
      className={`ext-alerts-filters slim-filters-and-selection-section${checkboxDependent ? ' message-on' : ''}`}
      style={{ position: 'relative' }}
    >
      <span {...blockedTitle}>
        {/* `uib-dropdown` is on this span upstream; `open` is what un-hides the menu. */}
        <span className={uibDropdown('slim-filters-action', sortOpen)}>
          <span
            className="cursor-pointer hover-lighter"
            role="button"
            aria-label="Sort"
            tabIndex={0}
            onClick={onToggleSort}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onToggleSort?.()
            }}
          >
            Sort <i className="fas fa-caret-down" />
          </span>
          {sortOpen ? (
            <ul className="dropdown-menu">
              {sortOptions.map((option) => (
                <li key={option.field}>
                  <a
                    href=""
                    className={`extension-candidates-sort-option${
                      option.display === currentSort ? ' extension-candidates-current-sort' : ''
                    }`}
                    onClick={(event) => {
                      event.preventDefault()
                      onSelectSort?.(option)
                    }}
                  >
                    {option.display}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </span>
      </span>

      <span {...blockedTitle}>
        <span className={`slim-filters-action${modified ? ' filters-different-from-default' : ''}`}>
          <span
            className="cursor-pointer hover-lighter"
            role="button"
            aria-label="Filters"
            tabIndex={0}
            onClick={onToggleFilters}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onToggleFilters?.()
            }}
          >
            {/* The count appears ONLY when something differs from the default, and it is a
                count of FILTERS (0-2), never of candidates. The trailing `&nbsp;` is upstream's
                and separates it from the caret. */}
            Filters{' '}
            {modified ? <span>({activeCount}){' '}</span> : null}
            <i className="fas fa-caret-down" />
          </span>
          {/* ⚠️ THE TOOLTIP CHROME IS LOAD-BEARING, exactly as it is for the alert panel
              (`SlimHeaderDropdown.panel`). Upstream opens this with `uib-tooltip-template` and
              `tooltip-class="extension-candidates-filters-tooltip-wrap"`, and
              `_extension-alert-candidates-slim.scss:765` hangs everything off
              `.extension-candidates-filters-tooltip-wrap.tooltip`: it forces `opacity: 1` over
              bootstrap's `.tooltip { opacity: 0 }`, strips the arrow's border, and turns
              `.tooltip-inner` from a 276px dark centred bubble into a white left-aligned panel
              at the filter width with no padding. Rendered bare, as this port had it, the menu
              inherited NONE of that and fell into the flow as a column of plain text under the
              bar — which is what it looked like on a real ATS page.

              `position: relative` is inline rather than in the stylesheet because it is not
              upstream's: there, `tooltip-append-to-body="true"` puts the panel on `<body>` and
              uib's positioning service computes page coordinates for it. Standing that up here
              would mean measuring the trigger on every open; anchoring to the trigger's own
              span gets the same result declaratively, and `top: 100%` is what "bottom" means.
              The wrap's own `left: … !important` already fixes the horizontal. */}

        </span>
      </span>

      <span className="slim-filters-total-results">
        <i className="fas fa-user" />
        {totalResults === undefined ? null : ` ${String(totalResults)}`}
      </span>

      {/* ⚠️ ANCHORED TO THE BAR, NOT TO THE "Filters" SPAN, AND THE STYLESHEET SAYS WHICH.
          `.extension-candidates-filters-tooltip-wrap` forces `left: 10px !important` and the
          panel is 180px wide inside a 200px frame — 10 + 180 + 10 is the frame, so that `left`
          is measured from the FRAME's edge. Upstream can write it that way because
          `tooltip-append-to-body="true"` puts the panel on `<body>`; hanging it off the Filters
          span instead made `left: 10px` mean "10px right of the word Filters", which put the
          menu two thirds of the way across the frame.

          The bar is full width, so it is the nearest thing to `<body>` that is still in this
          component. `position: relative` is inline rather than in the ported SCSS because it
          is not upstream's rule — it stands in for uib's positioning service, and putting it in
          the stylesheet would be a change to a file that is meant to be a verbatim copy. */}
      {filtersMenu === undefined || filtersMenu === null ? null : (
        <div className="extension-candidates-filters-tooltip-wrap tooltip bottom" style={{ top: '100%' }}>
          <div className="tooltip-arrow" />
          <div className="tooltip-inner">{filtersMenu}</div>
        </div>
      )}
    </div>
  )
}
